import { BaseQueryApi, fetchBaseQuery } from "@reduxjs/toolkit/query"
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from "@reduxjs/toolkit/query"
import { resetGlobalSlice, updateGlobalToastMessage } from "appRedux/slices/globalSlice"
import { resetUserProfileSlice } from "appRedux/slices/userSlice"
import {
  MAXIMUM_FILE_SIZE_ERROR,
  NOT_FOUND,
  SERVER_ERROR,
  TOO_MANY_REQUESTS,
  UNAUTHENTICATED_USER,
  UNAUTHORIZED_USER
} from "constants/errors"
import { NAVIGATE_TO_LOGIN_PAGE, SIGN_OUT_USER } from "constants/events"
import { ResponseError } from "types/general.types"
import { errorPatterns, makeFirstCharLowerCase, matchesPattern } from "utils/stringUtils"

export function getApiUrl(basePath: string) {
  return `${process.env.REACT_APP_API_URL}${basePath}`
}

const createBaseQuery = (basePath: string) => {
  return fetchBaseQuery({
    baseUrl: getApiUrl(basePath),
    credentials: "include"
  })
}

export const baseQueryWithMiddlewares = (basePath: string): BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> => {
  const baseQuery = createBaseQuery(basePath)

  return async (args, api, extraOptions) => {
    const result = await baseQuery(args, api, extraOptions)
    if (result.error) {
      if (result.error.status === 400) handleBadRequestError(api, result.error)
      else if (result.error.status === 401) {
        if (isPublicPage()) return result
        handleUnauthenticatedUserError(api, result.error)
      }
      else if (result.error.status === 403) handleUnauthorizedUserError(api, result.error)
      else if (result.error.status === 404) handleNotFoundError(api, result.error)
      else if (result.error.status === 413) handleMaximumSizePayloadError(api, result.error)
      else if (result.error.status === 429) handleTooManyRequestsError(api, result.error)
      else if (result.error.status === 500) handleInternalServerError(api, result.error)
    }
    return result
  }
}

export function isPublicPage(): boolean {
  return typeof window !== "undefined" && window.location.pathname.startsWith("/public")
}

export function getPublicAwareUrl(basePath: string, path: string): string {
  return isPublicPage() ? getApiUrl(`/public${basePath}${path}`) : path
}

export function getStaticAssetUrl(path: string): string {
  return isPublicPage() ? getApiUrl(`/public/${path}`) : getApiUrl(`/${path}`)
}

export function getPublicAwarePath(path: string): string {
  return isPublicPage() ? `/public${path}` : path
}

function handleBadRequestError(api: BaseQueryApi, error: ResponseError) {
  const message = getErrorMessageTranslationKey(error?.data)
  api.dispatch(updateGlobalToastMessage({
    severity: "error",
    key: UNAUTHENTICATED_USER,
    show: true,
    status: error.status,
    message: message
  }))
}

function handleUnauthenticatedUserError(api: BaseQueryApi, error: ResponseError) {
  // @ts-ignore
  const errorMessage = error?.data?.message || ""
  if (errorMessage) {
    const message = getErrorMessageTranslationKey(error?.data)
    setGlobalErrorMessage(api, UNAUTHORIZED_USER, message, error.status)
  } else if (typeof window !== "undefined") {
    api.dispatch(resetUserProfileSlice())
    api.dispatch(resetGlobalSlice())
    document.dispatchEvent(new CustomEvent(SIGN_OUT_USER))
    document.dispatchEvent(new CustomEvent(NAVIGATE_TO_LOGIN_PAGE, { detail: { referer: window.location.pathname } }))
  }
}

function handleUnauthorizedUserError(api: BaseQueryApi, error: ResponseError) {
  api.dispatch(updateGlobalToastMessage({
    severity: "error",
    key: UNAUTHORIZED_USER,
    show: true,
    status: error.status,
    message: "general.forbidden"
  }))
}

function handleNotFoundError(api: BaseQueryApi, error: ResponseError) {
  const message = getErrorMessageTranslationKey(error?.data)
  setGlobalErrorMessage(api, NOT_FOUND, message, error.status)
}

function handleMaximumSizePayloadError(api: BaseQueryApi, error: ResponseError) {
  api.dispatch(updateGlobalToastMessage({
    severity: "error",
    key: MAXIMUM_FILE_SIZE_ERROR,
    show: true,
    status: error.status,
    message: `general.${MAXIMUM_FILE_SIZE_ERROR}`
  }))
}

function handleTooManyRequestsError(api: BaseQueryApi, error: ResponseError) {
  setGlobalErrorMessage(api, TOO_MANY_REQUESTS, `errorMessage.${TOO_MANY_REQUESTS}`, error.status)
}

function handleInternalServerError(api: BaseQueryApi, error: ResponseError) {
  const message = getErrorMessageTranslationKey(error?.data)
  api.dispatch(updateGlobalToastMessage({
    severity: "error",
    key: SERVER_ERROR,
    show: true,
    status: error.status,
    message: message
  }))
}

function getErrorMessageTranslationKey(error: any) {
  const { message, code } = error

  let messageKey = `general.${SERVER_ERROR}`

  if (code) {
    return `errorMessage.${makeFirstCharLowerCase(code)}`
  } else if (message) {
    for (const key in errorPatterns) {
      if (matchesPattern(message, key as keyof typeof errorPatterns)) {
        messageKey = `errorMessage.${makeFirstCharLowerCase(key)}`
        break
      }
    }
  }

  return messageKey
}

function setGlobalErrorMessage(api: BaseQueryApi, key: string, message: string, status: number) {
  api.dispatch(updateGlobalToastMessage({
    severity: "error",
    key: key,
    show: true,
    status: status,
    message: message
  }))
}
